import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { database } from '../../firebase';
import { ref, get, set, push, remove, update } from 'firebase/database';
import Swal from 'sweetalert2';
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  XMarkIcon,
  CheckIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  ListBulletIcon,
  SparklesIcon,
  ClockIcon,
  DocumentTextIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';

const PackageConfiguration = () => {
  const { userData, currentUser } = useAuth();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    description: [],
    features: [],
    active: true,
    pricing: {
      weekly: 0,
      monthly: 0,
      quarterly: 0,
      biannual: 0,
      yearly: 0
    },
    duration: {
      weekly: 7,
      monthly: 30,
      quarterly: 120,
      biannual: 180,
      yearly: 365
    }
  });
  
  const [featureInput, setFeatureInput] = useState('');
  const [descriptionTitle, setDescriptionTitle] = useState('');
  const [descriptionContent, setDescriptionContent] = useState('');
  const [editingDescIndex, setEditingDescIndex] = useState(null);
  
  // Pricing options
  const pricingOptions = [
    { key: 'weekly', label: 'Weekly', duration: '7 days', icon: <ClockIcon className="h-4 w-4" /> },
    { key: 'monthly', label: 'Monthly', duration: '30 days', icon: <CalendarIcon className="h-4 w-4" /> },
    { key: 'quarterly', label: 'Quarterly', duration: '120 days', icon: <CalendarIcon className="h-4 w-4" /> },
    { key: 'biannual', label: 'Bi-Annual', duration: '180 days', icon: <CalendarIcon className="h-4 w-4" /> },
    { key: 'yearly', label: 'Yearly', duration: '365 days', icon: <CalendarIcon className="h-4 w-4" /> }
  ];
  
  // Fetch packages from database
  useEffect(() => {
    fetchPackages();
  }, []);
  
  const fetchPackages = async () => {
    setLoading(true);
    try {
      const packagesRef = ref(database, 'packages');
      const snapshot = await get(packagesRef);
      if (snapshot.exists()) {
        const packagesData = snapshot.val();
        const packagesList = Object.keys(packagesData).map(key => ({
          id: key,
          ...packagesData[key]
        }));
        setPackages(packagesList);
      } else {
        setPackages([]);
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load packages',
        timer: 2000,
        toast: true,
        position: 'top-end'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Add description section
  const addDescriptionSection = () => {
    // Only require at least one field (either title or content)
    if (descriptionTitle.trim() || descriptionContent.trim()) {
      setFormData({
        ...formData,
        description: [...formData.description, {
          title: descriptionTitle.trim(),
          content: descriptionContent.trim()
        }]
      });
      setDescriptionTitle('');
      setDescriptionContent('');
      setEditingDescIndex(null);
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Please add either a title or content (or both)',
        timer: 2000,
        toast: true,
        position: 'top-end'
      });
    }
  };
  
  // Edit description section
  const editDescriptionSection = (index) => {
    const section = formData.description[index];
    setDescriptionTitle(section.title);
    setDescriptionContent(section.content);
    setEditingDescIndex(index);
  };
  
  // Update description section
  const updateDescriptionSection = () => {
    if ((descriptionTitle.trim() || descriptionContent.trim()) && editingDescIndex !== null) {
      const updatedDescription = [...formData.description];
      updatedDescription[editingDescIndex] = {
        title: descriptionTitle.trim(),
        content: descriptionContent.trim()
      };
      setFormData({ ...formData, description: updatedDescription });
      setDescriptionTitle('');
      setDescriptionContent('');
      setEditingDescIndex(null);
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Please add either a title or content (or both)',
        timer: 2000,
        toast: true,
        position: 'top-end'
      });
    }
  };
  
  // Remove description section
  const removeDescriptionSection = (index) => {
    const newDescription = formData.description.filter((_, i) => i !== index);
    setFormData({ ...formData, description: newDescription });
  };
  
  // Add feature
  const addFeature = () => {
    if (featureInput.trim()) {
      setFormData({
        ...formData,
        features: [...formData.features, featureInput.trim()]
      });
      setFeatureInput('');
    }
  };
  
  // Remove feature
  const removeFeature = (index) => {
    const newFeatures = formData.features.filter((_, i) => i !== index);
    setFormData({ ...formData, features: newFeatures });
  };
  
  // Handle pricing change
  const handlePricingChange = (key, value) => {
    setFormData({
      ...formData,
      pricing: {
        ...formData.pricing,
        [key]: parseFloat(value) || 0
      }
    });
  };
  
  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      title: '',
      description: [],
      features: [],
      active: true,
      pricing: {
        weekly: 0,
        monthly: 0,
        quarterly: 0,
        biannual: 0,
        yearly: 0
      },
      duration: {
        weekly: 7,
        monthly: 30,
        quarterly: 120,
        biannual: 180,
        yearly: 365
      }
    });
    setFeatureInput('');
    setDescriptionTitle('');
    setDescriptionContent('');
    setEditingDescIndex(null);
    setEditingPackage(null);
    setIsFormOpen(false);
  };
  
  // Create or update package
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name.trim()) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Package name is required',
        timer: 2000,
        toast: true,
        position: 'top-end'
      });
      return;
    }
    
    if (!formData.title.trim()) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Package title is required',
        timer: 2000,
        toast: true,
        position: 'top-end'
      });
      return;
    }
    
    // Check if at least one pricing option has value
    const hasPricing = Object.values(formData.pricing).some(price => price > 0);
    if (!hasPricing) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'At least one pricing option must be set',
        timer: 2000,
        toast: true,
        position: 'top-end'
      });
      return;
    }
    
    setLoading(true);
    
    try {
      const packageData = {
        name: formData.name,
        title: formData.title,
        description: formData.description,
        features: formData.features,
        active: formData.active,
        pricing: formData.pricing,
        duration: formData.duration,
        createdAt: editingPackage ? formData.createdAt : Date.now(),
        updatedAt: Date.now(),
        createdBy: currentUser?.uid,
        createdByName: userData?.personalInfo?.name || 'System'
      };
      
      if (editingPackage) {
        // Update existing package
        const packageRef = ref(database, `packages/${editingPackage.id}`);
        await update(packageRef, packageData);
        Swal.fire({
          icon: 'success',
          title: 'Updated!',
          text: 'Package updated successfully',
          timer: 2000,
          toast: true,
          position: 'top-end'
        });
      } else {
        // Create new package
        const packagesRef = ref(database, 'packages');
        const newPackageRef = push(packagesRef);
        await set(newPackageRef, {
          ...packageData,
          id: newPackageRef.key
        });
        Swal.fire({
          icon: 'success',
          title: 'Created!',
          text: 'Package created successfully',
          timer: 2000,
          toast: true,
          position: 'top-end'
        });
      }
      
      resetForm();
      fetchPackages();
    } catch (error) {
      console.error('Error saving package:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to save package',
        timer: 2000,
        toast: true,
        position: 'top-end'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Edit package
  const handleEdit = (pkg) => {
    setEditingPackage(pkg);
    setFormData({
      name: pkg.name || '',
      title: pkg.title || '',
      description: pkg.description || [],
      features: pkg.features || [],
      active: pkg.active !== undefined ? pkg.active : true,
      pricing: pkg.pricing || {
        weekly: 0,
        monthly: 0,
        quarterly: 0,
        biannual: 0,
        yearly: 0
      },
      duration: pkg.duration || {
        weekly: 7,
        monthly: 30,
        quarterly: 120,
        biannual: 180,
        yearly: 365
      },
      createdAt: pkg.createdAt
    });
    setIsFormOpen(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Delete package
  const handleDelete = async (pkg) => {
    const result = await Swal.fire({
      title: 'Delete Package?',
      text: `Are you sure you want to delete "${pkg.title || pkg.name}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#800020',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel'
    });
    
    if (result.isConfirmed) {
      try {
        const packageRef = ref(database, `packages/${pkg.id}`);
        await remove(packageRef);
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Package deleted successfully',
          timer: 2000,
          toast: true,
          position: 'top-end'
        });
        fetchPackages();
      } catch (error) {
        console.error('Error deleting package:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to delete package',
          timer: 2000,
          toast: true,
          position: 'top-end'
        });
      }
    }
  };
  
  // Toggle package active status
  const handleToggleActive = async (pkg) => {
    try {
      const packageRef = ref(database, `packages/${pkg.id}`);
      await update(packageRef, { active: !pkg.active });
      fetchPackages();
      Swal.fire({
        icon: 'success',
        title: pkg.active ? 'Deactivated' : 'Activated',
        text: `Package ${pkg.active ? 'deactivated' : 'activated'} successfully`,
        timer: 1500,
        toast: true,
        position: 'top-end'
      });
    } catch (error) {
      console.error('Error toggling package:', error);
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Package Configuration</h1>
            <p className="text-gray-500 mt-1">Manage membership packages and pricing</p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setIsFormOpen(true);
            }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-all shadow-md hover:shadow-lg"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Create New Package</span>
          </button>
        </div>
        
        {/* Create/Edit Form Modal */}
        <AnimatePresence>
          {isFormOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mb-8"
            >
              <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-primary to-primary-dark px-6 py-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-semibold text-white">
                      {editingPackage ? 'Edit Package' : 'Create New Package'}
                    </h2>
                    <button
                      onClick={resetForm}
                      className="text-white hover:text-gray-200 transition-colors"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                </div>
                
                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                  {/* Basic Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Package Name *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="e.g., monthly, quarterly, yearly"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all"
                      />
                      <p className="text-xs text-gray-500 mt-1">Unique identifier for the package</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Package Title *
                      </label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="e.g., Monthly Membership, Quarterly Plan"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all"
                      />
                      <p className="text-xs text-gray-500 mt-1">Display name for customers</p>
                    </div>
                  </div>
                  
                  {/* Dynamic Description Sections */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description Sections
                    </label>
                    <div className="bg-gray-50 rounded-lg p-4 mb-3">
                      <div className="grid grid-cols-1 gap-3 mb-3">
                        <input
                          type="text"
                          value={descriptionTitle}
                          onChange={(e) => setDescriptionTitle(e.target.value)}
                          placeholder="Section Title (optional)"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all"
                        />
                        <textarea
                          value={descriptionContent}
                          onChange={(e) => setDescriptionContent(e.target.value)}
                          rows="3"
                          placeholder="Section Content (optional)"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all"
                        />
                      </div>
                      <div className="flex justify-end">
                        {editingDescIndex !== null ? (
                          <button
                            type="button"
                            onClick={updateDescriptionSection}
                            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                          >
                            Update Section
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={addDescriptionSection}
                            className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors flex items-center space-x-2"
                          >
                            <PlusIcon className="h-4 w-4" />
                            <span>Add Section</span>
                          </button>
                        )}
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      {formData.description.map((section, index) => (
                        <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              {section.title && (
                                <h4 className="font-semibold text-gray-900 mb-1">{section.title}</h4>
                              )}
                              {section.content && (
                                <p className="text-sm text-gray-600">{section.content}</p>
                              )}
                              {!section.title && !section.content && (
                                <p className="text-sm text-gray-400 italic">Empty section</p>
                              )}
                            </div>
                            <div className="flex items-center space-x-2 ml-4">
                              <button
                                type="button"
                                onClick={() => editDescriptionSection(index)}
                                className="text-blue-500 hover:text-blue-700 transition-colors"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => removeDescriptionSection(index)}
                                className="text-red-500 hover:text-red-700 transition-colors"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {formData.description.length === 0 && (
                      <p className="text-xs text-gray-400 mt-2">No description sections added yet. Add sections with title, content, or both.</p>
                    )}
                  </div>
                  
                  {/* Pricing Section */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Pricing (KES) *
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                      {pricingOptions.map((option) => (
                        <div key={option.key} className="relative">
                          <div className="flex items-center space-x-2 mb-1">
                            {option.icon}
                            <span className="text-sm font-medium text-gray-600">{option.label}</span>
                            <span className="text-xs text-gray-400">({option.duration})</span>
                          </div>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">KES</span>
                            <input
                              type="number"
                              value={formData.pricing[option.key]}
                              onChange={(e) => handlePricingChange(option.key, e.target.value)}
                              placeholder="0"
                              className="w-full pl-12 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Features Section */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Features
                    </label>
                    <div className="flex space-x-2 mb-3">
                      <input
                        type="text"
                        value={featureInput}
                        onChange={(e) => setFeatureInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && addFeature()}
                        placeholder="Enter a feature (e.g., 24/7 Access)"
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all"
                      />
                      <button
                        type="button"
                        onClick={addFeature}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        <PlusIcon className="h-5 w-5" />
                      </button>
                    </div>
                    
                    <div className="space-y-2">
                      {formData.features.map((feature, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg p-2">
                          <div className="flex items-center space-x-2">
                            <CheckIcon className="h-4 w-4 text-green-500" />
                            <span className="text-sm text-gray-700">{feature}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFeature(index)}
                            className="text-red-500 hover:text-red-700 transition-colors"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                    {formData.features.length === 0 && (
                      <p className="text-xs text-gray-400 mt-2">No features added yet. Add features to highlight what this package includes.</p>
                    )}
                  </div>
                  
                  {/* Active Status */}
                  <div className="flex items-center space-x-3">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.active}
                        onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                      <span className="ml-3 text-sm font-medium text-gray-700">Active Package</span>
                    </label>
                    <span className="text-xs text-gray-500">Inactive packages won't be shown to users</span>
                  </div>
                  
                  {/* Form Actions */}
                  <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={resetForm}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors shadow-md disabled:opacity-50"
                    >
                      {loading ? 'Saving...' : editingPackage ? 'Update Package' : 'Create Package'}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Packages List */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Available Packages</h2>
            <span className="text-sm text-gray-500">{packages.length} package(s)</span>
          </div>
          
          {loading && packages.length === 0 ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="text-gray-500 mt-2">Loading packages...</p>
            </div>
          ) : packages.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
              <SparklesIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No packages created yet</p>
              <button
                onClick={() => setIsFormOpen(true)}
                className="mt-3 text-primary hover:text-primary-dark font-medium"
              >
                Create your first package
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {packages.map((pkg) => (
                <motion.div
                  key={pkg.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border ${pkg.active ? 'border-gray-200' : 'border-gray-200 bg-gray-50/50'}`}
                >
                  <div className="p-5">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="text-lg font-bold text-gray-900">{pkg.title}</h3>
                          {!pkg.active && (
                            <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded-full">Inactive</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{pkg.name}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleToggleActive(pkg)}
                          className="p-2 text-gray-400 hover:text-primary transition-colors"
                          title={pkg.active ? 'Deactivate' : 'Activate'}
                        >
                          {pkg.active ? <EyeIcon className="h-4 w-4" /> : <EyeSlashIcon className="h-4 w-4" />}
                        </button>
                        <button
                          onClick={() => handleEdit(pkg)}
                          className="p-2 text-gray-400 hover:text-blue-500 transition-colors"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(pkg)}
                          className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    
                    {/* Dynamic Description Sections */}
                    {pkg.description && pkg.description.length > 0 && (
                      <div className="mb-4">
                        <div className="flex items-center space-x-2 mb-2">
                          <DocumentTextIcon className="h-4 w-4 text-primary" />
                          <span className="text-xs font-medium text-gray-600">Description</span>
                        </div>
                        <div className="space-y-3">
                          {pkg.description.map((section, idx) => (
                            <div key={idx}>
                              {section.title && (
                                <h4 className="font-semibold text-gray-800 text-sm mb-1">{section.title}</h4>
                              )}
                              {section.content && (
                                <p className="text-sm text-gray-600">{section.content}</p>
                              )}
                              {!section.title && !section.content && (
                                <p className="text-sm text-gray-400 italic">No content</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Pricing */}
                    <div className="mb-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <CurrencyDollarIcon className="h-4 w-4 text-primary" />
                        <span className="text-xs font-medium text-gray-600">Pricing</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(pkg.pricing || {}).map(([key, price]) => {
                          if (price > 0) {
                            const option = pricingOptions.find(opt => opt.key === key);
                            return (
                              <div key={key} className="flex justify-between items-center text-sm">
                                <span className="text-gray-600">{option?.label}:</span>
                                <span className="font-semibold text-gray-900">KES {price.toLocaleString()}</span>
                              </div>
                            );
                          }
                          return null;
                        })}
                      </div>
                    </div>
                    
                    {/* Features */}
                    {pkg.features && pkg.features.length > 0 && (
                      <div>
                        <div className="flex items-center space-x-2 mb-2">
                          <ListBulletIcon className="h-4 w-4 text-primary" />
                          <span className="text-xs font-medium text-gray-600">Features</span>
                        </div>
                        <div className="space-y-1">
                          {pkg.features.slice(0, 4).map((feature, idx) => (
                            <div key={idx} className="flex items-center space-x-2 text-sm">
                              <CheckIcon className="h-3 w-3 text-green-500 flex-shrink-0" />
                              <span className="text-gray-600">{feature}</span>
                            </div>
                          ))}
                          {pkg.features.length > 4 && (
                            <p className="text-xs text-gray-400">+{pkg.features.length - 4} more features</p>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Meta Info */}
                    <div className="mt-4 pt-3 border-t border-gray-100">
                      <p className="text-xs text-gray-400">
                        Created: {new Date(pkg.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PackageConfiguration;